import React, { useState, useMemo } from 'react';
import { UserProfile, Room, CreateUserProfileData, UserRole } from '@shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';

interface UsersTabProps {
  users: UserProfile[];
  rooms: Room[];
}

export function UsersTab({ users, rooms }: UsersTabProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Filter state
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    alias: '',
    role: '',
    roomId: '',
    created: ''
  });

  // Calculate filtered and paginated users
  const { paginatedUsers, totalPages, startIndex, endIndex, totalFiltered } = useMemo(() => {
    // Apply filters
    let filtered = users.filter(user => {
      const nameMatch = !filters.name || user.name.toLowerCase().includes(filters.name.toLowerCase());
      const emailMatch = !filters.email || user.email.toLowerCase().includes(filters.email.toLowerCase());
      const aliasMatch = !filters.alias || (user.alias && user.alias.toLowerCase().includes(filters.alias.toLowerCase()));
      const roleMatch = !filters.role || user.role === filters.role;
      const roomMatch = !filters.roomId || user.roomId === filters.roomId;
      
      // Date filter - check if created_at matches the filter string
      const createdMatch = !filters.created || (
        user.created_at && 
        new Date(user.created_at.seconds * 1000).toLocaleDateString().includes(filters.created)
      );

      return nameMatch && emailMatch && aliasMatch && roleMatch && roomMatch && createdMatch;
    });

    // Apply pagination
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const paginated = filtered.slice(start, end);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    return {
      filteredUsers: filtered,
      paginatedUsers: paginated,
      totalPages,
      startIndex: start + 1,
      endIndex: Math.min(start + filtered.length, end),
      totalFiltered: filtered.length
    };
  }, [users, filters, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      email: '',
      alias: '',
      role: '',
      roomId: '',
      created: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handleUpdateUserRole = async (userId: string, newRole: string, roomId?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const updateUserRole = httpsCallable(functions, 'updateUserRole');
      const result = await updateUserRole({
        userId,
        role: newRole,
        roomId
      });
      
      alert((result.data as { message: string }).message);
      
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      
      const deleteUser = httpsCallable(functions, 'deleteUser');
      const result = await deleteUser({ userId });
      alert((result.data as { message: string }).message);
      
    } catch (err: unknown) {
      let errorMessage = 'Failed to delete user';
      
      if (err && typeof err === 'object' && 'code' in err && err.code === 'functions/internal') {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const baseClasses = 'badge';
    switch (role) {
      case 'superadmin':
        return `${baseClasses} badge-danger`;
      case 'admin':
        return `${baseClasses} badge-info`;
      case 'player':
        return `${baseClasses} badge-success`;
      default:
        return `${baseClasses} badge-info`;
    }
  };

  const getRoomName = (roomId?: string) => {
    if (!roomId) return 'No room assigned';
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : `Room ${roomId}`;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{startIndex}</span> to{' '}
          <span className="font-medium">{endIndex}</span> of{' '}
          <span className="font-medium">{totalFiltered}</span> users
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {pageNumbers.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                currentPage === pageNum
                  ? 'bg-primary text-white'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {pageNum}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Users Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage user roles and room assignments
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
            Create User
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Filter Controls */}
        <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Search & Filter</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary/80"
              >
                Clear Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <input
                type="text"
                placeholder="Search by name"
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Search by email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Search by alias"
                value={filters.alias}
                onChange={(e) => handleFilterChange('alias', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary bg-white"
              >
                <option value="">All Roles</option>
                <option value="player">Player</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>
            <div>
              <select
                value={filters.roomId}
                onChange={(e) => handleFilterChange('roomId', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary bg-white"
              >
                <option value="">All Rooms</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <input
                type="text"
                placeholder="Filter by date"
                value={filters.created}
                onChange={(e) => handleFilterChange('created', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Poker Alias
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Room Assignment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    {hasActiveFilters 
                      ? 'No users match the current filters. Clear filters to see all users.'
                      : 'No users found. Create your first user to get started.'
                    }
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, index) => (
                  <tr key={user.uid || `user-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name}
                          </div>
                          {user.phone && (
                            <div className="text-sm text-gray-500">
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.alias || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getRoleBadge(user.role)}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.role === 'admin' ? getRoomName(user.roomId) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.created_at ? new Date(user.created_at.seconds * 1000).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="text-primary hover:text-primary/80"
                          disabled={loading}
                        >
                          Edit
                        </button>
                        {user.role !== 'superadmin' && (
                          <button
                            onClick={() => {
                              if (!user.uid) {
                                alert('Error: User ID is missing. Cannot delete user.');
                                return;
                              }
                              handleDeleteUser(user.uid, user.name);
                            }}
                            className="text-red-600 hover:text-red-800"
                            disabled={loading}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}

        {/* Edit User Modal */}
        {editingUser && (
          <EditUserModal
            user={editingUser}
            rooms={rooms}
            onSave={(userId, role, roomId) => {
              handleUpdateUserRole(userId, role, roomId);
              setEditingUser(null);
            }}
            onCancel={() => setEditingUser(null)}
            loading={loading}
          />
        )}

        {/* Create User Modal */}
        {showCreateForm && (
          <CreateUserModal
            rooms={rooms}
            onSave={() => {
              setShowCreateForm(false);
            }}
            onCancel={() => setShowCreateForm(false)}
            loading={loading}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

// Edit User Modal Component
interface EditUserModalProps {
  user: UserProfile;
  rooms: Room[];
  onSave: (userId: string, role: string, roomId?: string) => void;
  onCancel: () => void;
  loading: boolean;
}

function EditUserModal({ user, rooms, onSave, onCancel, loading }: EditUserModalProps) {
  const [role, setRole] = useState(user.role);
  const [roomId, setRoomId] = useState(user.roomId || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(user.uid, role, roomId || undefined);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit User: {user.name}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="input"
            >
              <option value="player">Player</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>

          {role === 'admin' && (
            <div>
              <label className="label">Room Assignment</label>
              <select
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                className="input"
              >
                <option value="">Select a room</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create User Modal Component
interface CreateUserModalProps {
  rooms: Room[];
  onSave: (userData: CreateUserProfileData) => void;
  onCancel: () => void;
  loading: boolean;
}

function CreateUserModal({ rooms, onSave, onCancel, loading }: CreateUserModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'player',
    roomId: ''
  });
  const [, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const createUser = httpsCallable(functions, 'createUser');
      const result = await createUser(formData);
      
      alert((result.data as { message: string }).message);
      onSave({
        ...formData,
        role: formData.role as UserRole
      });
      
    } catch (err: unknown) {
      let errorMessage = 'Failed to create user';
      
      if (err && typeof err === 'object' && 'code' in err && err.code === 'auth/email-already-exists') {
        errorMessage = 'This email address is already in use. Please use a different email.';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Create New User
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Phone (Optional)</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
            >
              <option value="player">Player</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>

          {formData.role === 'admin' && (
            <div>
              <label className="label">Room Assignment</label>
              <select
                value={formData.roomId}
                onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}
                className="input"
              >
                <option value="">Select a room</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
